import { ChannelIcon, CopyButton, Select, Tip, useFormat } from '@ly360/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Bell,
  CalendarClock,
  Megaphone,
  Minus,
  Pencil,
  Plus,
  RefreshCw,
  ShoppingBag,
  Sparkles,
} from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { RETAIL_KEYS, shelfPrice, useModule, useProducts, type CommerceProfile } from './data';
import { NewOrderDialog, productOption } from './OrderForm';
import { ClassBadge, PlatformLabel, SourceLabel } from './ui';

/**
 * What this customer buys, beside the conversation or on their record: how valuable they are, what they like, what
 * is about to run out and what they are waiting for, with "record a sale" for sales made in chat.
 */
export function PurchasesPanel({
  personId,
  conversationId,
  channel,
  compact,
}: {
  personId: string;
  conversationId?: string;
  channel?: string;
  compact?: boolean;
}) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const { can } = useAuth();
  const enabled = useModule('commerce') && can('commerce:read');
  const [selling, setSelling] = useState<false | { productId: string; quantity: number }[]>(false);
  const [sold, setSold] = useState<string | null>(null);
  const q = useQuery({
    queryKey: ['commerce', personId],
    enabled,
    queryFn: () => api<CommerceProfile>(`/v1/people/${personId}/commerce`),
  });
  const suggested = !!useSuggestion(enabled && can('commerce:write') ? conversationId : undefined).data
    ?.suggestion?.lines.length;
  if (!enabled || !q.data) return null;
  const p = q.data;
  const money = (m: number | string) => fmt.money(Number(m), p.currency);
  const Wrap = compact ? 'div' : 'section';

  return (
    <Wrap className={compact ? 'cp-section purchases' : 'card card-pad purchases'} data-testid="purchases">
      <div className="purchases-head">
        <h3 className={compact ? undefined : 'section-title'}>{t('retail.purchases.title')}</h3>
        <ClassBadge value={p.class} />
      </div>
      <dl className="purchase-stats">
        <div>
          <dt>{t('retail.purchases.spent')}</dt>
          <dd>{money(p.spentMinor)}</dd>
        </div>
        <div>
          <dt>{t('retail.purchases.orders')}</dt>
          <dd>{fmt.number(p.orders)}</dd>
        </div>
        <div>
          <dt>{t('retail.purchases.last')}</dt>
          <dd>{p.lastOrderAt ? fmt.relative(p.lastOrderAt) : '—'}</dd>
        </div>
      </dl>
      {(p.favouriteBrands.length > 0 || p.favouriteFamilies.length > 0) && (
        <div className="chips" style={{ marginBottom: '0.5rem' }} aria-label={t('retail.purchases.likes')}>
          {p.favouriteFamilies.map((f) => (
            <span key={f} className="chip">
              {t(`retail.family.${f}`)}
            </span>
          ))}
          {p.favouriteBrands.map((b) => (
            <span key={b} className="chip" dir="auto">
              {b}
            </span>
          ))}
        </div>
      )}
      {(p.preferredStoreName || p.birthday) && (
        <p className="faint purchases-meta">
          {p.preferredStoreName && (
            <span dir="auto">{t('retail.purchases.store', { store: p.preferredStoreName })}</span>
          )}
          {p.birthday && (
            <span>
              <CalendarClock size={12} aria-hidden="true" />{' '}
              {fmt.dayMonth(`${p.birthday.slice(0, 10)}T12:00:00Z`)}
            </span>
          )}
        </p>
      )}
      {p.replenishDue.map((r) => (
        <div key={r.productId} className="notice warn purchases-due" data-testid="replenish-due">
          <RefreshCw size={14} aria-hidden="true" />
          <span className="grow" dir="auto">
            {t('retail.purchases.runningOut', {
              product: `${r.brand} ${r.name}`,
              date: fmt.date(r.boughtAt),
            })}{' '}
            <strong>{money(r.priceMinor)}</strong>
          </span>
        </div>
      ))}
      {p.waitlist.map((w) => (
        <div key={w.productId} className="notice purchases-due">
          <Bell size={14} aria-hidden="true" />
          <span className="grow" dir="auto">
            {t(w.stock ? 'retail.purchases.waitingBack' : 'retail.purchases.waiting', {
              product: `${w.brand} ${w.name}`,
            })}
          </span>
        </div>
      ))}
      {p.recentOrders.length > 0 && (
        <ul className="list-plain cp-list" data-testid="recent-orders">
          {p.recentOrders.slice(0, compact ? 3 : 10).map((o) => (
            <li key={o.id}>
              <ChannelIcon channel={o.source} size={12} />
              <span
                className="grow ellipsis"
                dir="auto"
                title={(o.items ?? []).map((i) => i.name).join(', ')}
              >
                {(o.items ?? []).map((i) => i.name).join(', ')}
              </span>
              <span className="faint">{fmt.date(o.placedAt)}</span>
              <strong className="num">{money(o.totalMinor)}</strong>
            </li>
          ))}
        </ul>
      )}
      {!compact && p.recentOrders.length > 0 && (
        <div className="table-wrap" style={{ marginTop: '0.75rem' }}>
          <table className="table table-responsive">
            <tbody>
              {p.recentOrders.map((o) => (
                <tr key={o.id}>
                  <td className="mono">{o.number}</td>
                  <td>
                    <SourceLabel source={o.source} />
                  </td>
                  <td>
                    {o.campaignId ? (
                      <Link to={`/campaigns/${o.campaignId}`} dir="auto">
                        {o.campaignName}
                      </Link>
                    ) : (
                      <span className="faint">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {sold && (
        <p className="notice success" role="status" data-testid="sale-recorded">
          {t('retail.purchases.recorded', { number: sold })}
        </p>
      )}
      {conversationId && can('commerce:write') && (
        <SuggestedOrder
          personId={personId}
          conversationId={conversationId}
          channel={channel}
          onEdit={(lines) => setSelling(lines)}
          onRecorded={setSold}
        />
      )}
      {can('commerce:write') && (
        <div className="purchases-actions">
          <button
            className={`btn btn-sm${suggested ? '' : ' btn-grad'}`}
            onClick={() => setSelling([])}
            data-testid="record-sale"
          >
            <ShoppingBag size={14} />
            {suggested ? t('retail.purchases.recordOther') : t('retail.purchases.recordSale')}
          </button>
          <WaitlistButton personId={personId} channel={channel} />
        </div>
      )}
      {compact && <ProductLookup />}
      {selling && (
        <NewOrderDialog
          onClose={() => setSelling(false)}
          personId={personId}
          source={channel}
          conversationId={conversationId}
          initialLines={selling}
          onCreated={(_id, number) => setSold(number)}
        />
      )}
    </Wrap>
  );
}

/** "Tell me when it is back": add the customer to a sold-out product's waitlist. */
function WaitlistButton({ personId, channel }: { personId: string; channel?: string }) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const qc = useQueryClient();
  const products = useProducts();
  const [value, setValue] = useState('');
  const add = useMutation({
    mutationFn: (productId: string) =>
      api(`/v1/products/${productId}/waitlist`, {
        method: 'POST',
        body: JSON.stringify({ personId, channel: channel ?? null }),
      }),
    onSuccess: () => {
      setValue('');
      for (const k of RETAIL_KEYS) void qc.invalidateQueries({ queryKey: [k] });
    },
  });
  const soldOut = (products.data?.items ?? []).filter((p) => p.stock === 0);
  if (!soldOut.length) return null;
  return (
    <Tip label={t('retail.purchases.notifyWhenBack')} className="meta-field" side="end">
      <Bell size={14} aria-hidden="true" className="faint" />
      <Select
        value={value}
        onChange={(v) => {
          setValue(v);
          if (v) add.mutate(v);
        }}
        placeholder={t('retail.purchases.waitlistShort')}
        aria-label={t('retail.purchases.notifyWhenBack')}
        triggerClassName="select-pill"
        data-testid="waitlist-add"
        options={soldOut.map((p) => ({
          value: p.id,
          label: `${p.brand} ${p.name}`,
          hint: t('retail.opp.waiting', { count: p.waiting, value: fmt.number(p.waiting) }),
        }))}
      />
    </Tip>
  );
}

/** Quick catalogue lookup while chatting: price, stock and a line to paste into the reply. */
function ProductLookup() {
  const { t } = useTranslation();
  const fmt = useFormat();
  const products = useProducts();
  const [id, setId] = useState('');
  const p = products.data?.items.find((x) => x.id === id);
  const money = (m: number) => fmt.money(m, p?.currency ?? 'QAR');
  const line = p
    ? `${p.brand} ${p.name}${p.sizeMl ? ` ${p.sizeMl} ml` : ''}: ${money(shelfPrice(p))}${
        p.salePriceMinor && Number(p.salePriceMinor) < Number(p.priceMinor)
          ? ` (${t('retail.lookup.was')} ${money(Number(p.priceMinor))})`
          : ''
      }`
    : '';
  return (
    <div className="product-lookup" data-testid="product-lookup">
      <h3>{t('retail.lookup.title')}</h3>
      <Select
        value={id}
        onChange={setId}
        placeholder={t('retail.order.searchProduct')}
        aria-label={t('retail.lookup.title')}
        options={(products.data?.items ?? []).map((x) => ({
          ...productOption(x, t, (m) => fmt.money(m, x.currency)),
          disabled: false,
        }))}
      />
      {p && (
        <div className="lookup-result">
          <span dir="auto">{line}</span>
          <span className={p.stock === 0 ? 'overdue' : 'faint'}>
            {p.stock === 0
              ? t('retail.stock.outWaiting', { count: p.waiting })
              : p.stock === null
                ? ''
                : t('retail.stock.left', { count: p.stock })}
          </span>
          <CopyButton value={line} />
        </div>
      )}
    </div>
  );
}

/** "Started from: TikTok ad · Oud season" on a conversation. */
export function ConversationOrigin({
  origin,
}: {
  origin: {
    campaignId: string | null;
    campaignName: string | null;
    platform: string | null;
    kind: string | null;
    adRef: { headline: string | null; sourceType: string | null } | null;
  };
}) {
  const { t } = useTranslation();
  return (
    <div className="origin" data-testid="conversation-origin">
      <Megaphone size={13} aria-hidden="true" />
      <span className="faint">
        {t(origin.kind === 'broadcast' ? 'retail.origin.reply' : 'retail.origin.ad')}
      </span>
      {origin.platform && <PlatformLabel platform={origin.platform} />}
      {origin.campaignId ? (
        <Link to={`/campaigns/${origin.campaignId}`} dir="auto" className="ellipsis">
          {origin.campaignName}
        </Link>
      ) : (
        <span dir="auto" className="ellipsis">
          {origin.adRef?.headline ?? t('retail.origin.unknownAd')}
        </span>
      )}
    </div>
  );
}

/** What the customer in this chat asked to buy (refreshed as they write). */
function useSuggestion(conversationId: string | undefined) {
  return useQuery({
    queryKey: ['order-suggestion', conversationId],
    enabled: !!conversationId,
    queryFn: () =>
      api<{ suggestion: Suggestion | null }>(`/v1/conversations/${conversationId}/order-suggestion`),
    refetchInterval: 15_000,
  });
}

interface Suggestion {
  lines: {
    productId: string;
    label: string;
    quantity: number;
    quantityStated: boolean;
    unitPriceMinor: number;
    regularPriceMinor: number;
    stock: number | null;
    quotedMinor: number | null;
    priceDiffers: boolean;
    alternatives: { productId: string; label: string; unitPriceMinor: number; stock: number | null }[];
  }[];
  currency: string;
  campaign: { id: string; name: string } | null;
}

/**
 * "The customer asked for 2 × Versace Eros": read from their own message, priced from the catalogue and credited to
 * the campaign behind the chat, so a sale made in chat is recorded with one click and no typing.
 */
function SuggestedOrder({
  personId,
  conversationId,
  channel,
  onEdit,
  onRecorded,
}: {
  personId: string;
  conversationId: string;
  channel?: string;
  onEdit: (lines: { productId: string; quantity: number }[]) => void;
  onRecorded: (number: string) => void;
}) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const qc = useQueryClient();
  const q = useSuggestion(conversationId);
  // The person can change quantities or pick another size before recording.
  const [edits, setEdits] = useState<Record<number, { productId?: string; quantity?: number }>>({});
  const s = q.data?.suggestion;
  const record = useMutation({
    mutationFn: (items: { productId: string; quantity: number }[]) =>
      api<{ id: string; number: string }>('/v1/orders', {
        method: 'POST',
        body: JSON.stringify({ personId, source: channel ?? 'whatsapp', items, conversationId }),
      }),
    onSuccess: (r) => {
      setEdits({});
      for (const k of [...RETAIL_KEYS, 'order-suggestion', 'commerce'])
        void qc.invalidateQueries({ queryKey: [k] });
      onRecorded(r.number);
    },
  });
  if (!s?.lines.length) return null;
  const money = (m: number) => fmt.money(m, s.currency);
  const lines = s.lines.map((l, i) => {
    const e = edits[i] ?? {};
    const alt = e.productId ? l.alternatives.find((a) => a.productId === e.productId) : undefined;
    return {
      ...l,
      productId: alt?.productId ?? l.productId,
      label: alt?.label ?? l.label,
      unitPriceMinor: alt?.unitPriceMinor ?? l.unitPriceMinor,
      stock: alt ? alt.stock : l.stock,
      quantity: e.quantity ?? l.quantity,
      swapped: !!alt,
      original: { productId: l.productId, label: l.label },
    };
  });
  const total = lines.reduce((sum, l) => sum + l.unitPriceMinor * l.quantity, 0);
  const short = lines.some((l) => l.stock !== null && l.stock < l.quantity);
  const items = lines.map((l) => ({ productId: l.productId, quantity: l.quantity }));

  return (
    <div className="suggested-order" data-testid="suggested-order">
      <div className="suggested-head">
        <Sparkles size={14} aria-hidden="true" />
        <strong>{t('retail.suggest.title')}</strong>
      </div>
      <ul className="list-plain suggested-lines">
        {lines.map((l, i) => (
          <li key={i}>
            <div className="suggested-name" dir="auto">
              {l.label}
            </div>
            <div className="suggested-line">
              <span className="qty-stepper" role="group" aria-label={t('retail.suggest.quantity')}>
                <button
                  type="button"
                  className="icon-btn"
                  aria-label={t('retail.suggest.less')}
                  disabled={l.quantity <= 1}
                  onClick={() => setEdits({ ...edits, [i]: { ...edits[i], quantity: l.quantity - 1 } })}
                >
                  <Minus size={12} />
                </button>
                <span className="num" data-testid="suggested-qty">
                  {fmt.number(l.quantity)}
                </span>
                <button
                  type="button"
                  className="icon-btn"
                  aria-label={t('retail.suggest.more')}
                  onClick={() => setEdits({ ...edits, [i]: { ...edits[i], quantity: l.quantity + 1 } })}
                >
                  <Plus size={12} />
                </button>
              </span>
              <span className="grow" />
              <strong className="num">{money(l.unitPriceMinor * l.quantity)}</strong>
            </div>
            <div className="faint suggested-notes">
              {t('retail.suggest.each', { price: money(l.unitPriceMinor) })}
              {l.unitPriceMinor < l.regularPriceMinor && !l.swapped && (
                <> · {t('retail.suggest.onSale', { price: money(l.regularPriceMinor) })}</>
              )}
            </div>
            {l.priceDiffers && !l.swapped && l.quotedMinor !== null && (
              <p className="notice warn suggested-warn" role="status">
                {t('retail.suggest.quotedDiffers', {
                  quoted: money(l.quotedMinor),
                  price: money(l.unitPriceMinor),
                })}
              </p>
            )}
            {l.stock !== null && l.stock < l.quantity && (
              <p className="notice warn suggested-warn" role="status">
                {l.stock === 0 ? t('retail.suggest.out') : t('retail.suggest.low', { count: l.stock })}
              </p>
            )}
            {(l.alternatives.length > 0 || l.swapped) && (
              <div className="chips suggested-alts">
                <span className="faint">{t('retail.suggest.orThis')}</span>
                {(l.swapped ? [{ productId: l.original.productId, label: l.original.label }] : [])
                  .concat(l.alternatives.filter((a) => a.productId !== l.productId))
                  .map((a) => (
                    <button
                      key={a.productId}
                      type="button"
                      className="chip chip-button"
                      dir="auto"
                      onClick={() =>
                        setEdits({
                          ...edits,
                          [i]: {
                            ...edits[i],
                            productId: a.productId === s.lines[i]!.productId ? undefined : a.productId,
                          },
                        })
                      }
                    >
                      {a.label}
                    </button>
                  ))}
              </div>
            )}
          </li>
        ))}
      </ul>
      {s.campaign && (
        <p className="faint suggested-credit">
          <Megaphone size={12} aria-hidden="true" /> {t('retail.suggest.credited')}{' '}
          <Link to={`/campaigns/${s.campaign.id}`} dir="auto">
            {s.campaign.name}
          </Link>
        </p>
      )}
      {record.isError && <p className="notice warn">{(record.error as Error).message}</p>}
      <div className="purchases-actions">
        <button
          className="btn btn-sm btn-grad"
          disabled={record.isPending || short}
          onClick={() => record.mutate(items)}
          data-testid="record-suggested"
        >
          <ShoppingBag size={14} />
          {t('retail.suggest.record', { total: money(total) })}
        </button>
        <button className="btn btn-sm" onClick={() => onEdit(items)} data-testid="edit-suggested">
          <Pencil size={14} />
          {t('retail.suggest.edit')}
        </button>
      </div>
    </div>
  );
}
